import { useRef, useState, useLayoutEffect } from 'react'
import { Button } from '@/components/Button'
import { useContainerWidth } from '@/hooks/useContainerWidth'

interface PaginationProps {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
	maxVisible?: number
	className?: string
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end'

/** Button size='md' is 40px wide (p-7 + border-1 + 24px icon); gap-1 token = 1px. */
const ICON_W = 40
const GAP = 1
const ELLIPSIS_W = 24

/**
 * Picks the densest control set that fits `width`: First/Last drop first, then
 * the widest numbered window that fits wins, preferring anchor pages + ellipses
 * when they also fit. Prev/Next and the current page are the floor.
 */
function resolveTier (
	width: number,
	pageW: number,
	currentPage: number,
	totalPages: number,
	maxVisible: number
): { showEdges: boolean; showAnchors: boolean; count: number } {
	// Cost the list that would actually render, so anchor pages and ellipses
	// are counted exactly rather than estimated.
	const costFor = (count: number, showAnchors: boolean) =>
		getVisiblePages(currentPage, totalPages, count, showAnchors).reduce<number>(
			(sum, item) => sum + (typeof item === 'number' ? pageW : ELLIPSIS_W) + GAP,
			0
		)

	const fits = (count: number, showAnchors: boolean, showEdges: boolean) =>
		costFor(count, showAnchors) <= width - (showEdges ? 4 : 2) * (ICON_W + GAP)

	if (fits(maxVisible, true, true)) {
		return { showEdges: true, showAnchors: true, count: maxVisible }
	}

	// Count outermost so a wider container never shows fewer numbered pages.
	for (let count = maxVisible; count >= 1; count--) {
		for (const showAnchors of [true, false]) {
			if (fits(count, showAnchors, false)) {
				return { showEdges: false, showAnchors, count }
			}
		}
	}

	return { showEdges: false, showAnchors: false, count: 1 }
}

function getVisiblePages (
	currentPage: number,
	totalPages: number,
	maxVisible: number,
	showAnchors: boolean
): PageItem[] {
	if (totalPages <= maxVisible) {
		return Array.from({ length: totalPages }, (_, i) => i + 1)
	}

	const start = Math.max(
		1,
		Math.min(
			currentPage - Math.floor((maxVisible - 1) / 2),
			totalPages - maxVisible + 1
		)
	)
	const end = start + maxVisible - 1

	const pageWindow: PageItem[] = []
	for (let i = start; i <= end; i++) {
		pageWindow.push(i)
	}

	if (!showAnchors) return pageWindow

	const pages: PageItem[] = []

	if (start > 1) {
		pages.push(1)
		if (start > 2) pages.push('ellipsis-start')
	}

	pages.push(...pageWindow)

	if (end < totalPages) {
		if (end < totalPages - 1) pages.push('ellipsis-end')
		pages.push(totalPages)
	}

	return pages
}

export function Pagination ({
	currentPage,
	totalPages,
	onPageChange,
	maxVisible = 4,
	className = ''
}: PaginationProps) {
	const { ref: navRef, width, measured } = useContainerWidth<HTMLElement>()
	const measureRef = useRef<HTMLDivElement>(null)
	const [pageW, setPageW] = useState(0)

	// Measured off a hidden button so 3-digit page numbers (which exceed
	// min-w-40) are accounted for without measuring the visible row.
	useLayoutEffect(() => {
		if (measureRef.current) setPageW(measureRef.current.offsetWidth)
	}, [totalPages])

	if (totalPages <= 1) return null

	const ready = measured && pageW > 0
	const { showEdges, showAnchors, count } = ready
		? resolveTier(width, pageW, currentPage, totalPages, maxVisible)
		: { showEdges: true, showAnchors: true, count: maxVisible }

	const pages = getVisiblePages(currentPage, totalPages, count, showAnchors)
	const edgeClass = showEdges ? 'spaces:contents' : 'spaces:hidden'

	return (
		<nav
			ref={navRef}
			aria-label='Pagination'
			className={`spaces-pagination spaces:relative spaces:flex spaces:items-center spaces:justify-center ${className}`}
		>
			<div
				ref={measureRef}
				aria-hidden='true'
				className='spaces:absolute spaces:invisible spaces:pointer-events-none'
			>
				<Button
					label={String(totalPages)}
					appearance='ghost'
					color='content2'
					size='md'
					tabIndex={-1}
				/>
			</div>

			<div className='spaces:flex spaces:flex-wrap spaces:items-center spaces:justify-center spaces:gap-1'>
				{/* Edge buttons stay mounted when hidden so E2E selectors can reach them. */}
				<span className={edgeClass}>
					<Button
						iconOnly='CaretFirst'
						appearance='ghost'
						color='content4'
						size='md'
						aria-label='First page'
						disabled={currentPage === 1}
						onClick={() => onPageChange(1)}
					/>
				</span>
				<Button
					iconOnly='CaretLeft'
					appearance='ghost'
					color='content4'
					size='md'
					aria-label='Previous page'
					disabled={currentPage === 1}
					onClick={() => onPageChange(currentPage - 1)}
				/>

				{pages.map(item =>
					typeof item === 'number' ? (
						<Button
							key={item}
							label={String(item)}
							appearance={item === currentPage ? 'subtle' : 'ghost'}
							color={item === currentPage ? 'brand' : 'content2'}
							size='md'
							aria-label={`Page ${item}`}
							aria-current={item === currentPage ? 'page' : undefined}
							onClick={() => onPageChange(item)}
						/>
					) : (
						<span
							key={item}
							className='spaces:text-14/24 spaces:text-content-4 spaces:px-4 spaces:select-none'
							aria-hidden='true'
						>
							…
						</span>
					)
				)}

				<Button
					iconOnly='CaretRight'
					appearance='ghost'
					color='content4'
					size='md'
					aria-label='Next page'
					disabled={currentPage === totalPages}
					onClick={() => onPageChange(currentPage + 1)}
				/>
				<span className={edgeClass}>
					<Button
						iconOnly='CaretLast'
						appearance='ghost'
						color='content4'
						size='md'
						aria-label='Last page'
						disabled={currentPage === totalPages}
						onClick={() => onPageChange(totalPages)}
					/>
				</span>
			</div>
		</nav>
	)
}
